import React, { useEffect, useState, Suspense, lazy } from "react";
import {
  Box,
  SimpleGrid,
  Icon,
  Spinner,
  Center,
} from "@chakra-ui/react";
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import { MdAddTask, MdBarChart, MdFileCopy } from "react-icons/md";
import {
  columnsDataAssiduity,
} from "views/admin/enterprise/variables/columnsData";
import useEnterpriseService from "services/enterpriseService";

// Lazy load components
const ManagersAssiduityTable = lazy(() => import("views/admin/enterprise/components/ManagersAssiduityTable"));
const TotalSpent = lazy(() => import("views/admin/enterprise/components/TotalSpent"));
const CheckTable = lazy(() => import("views/admin/enterprise/components/CheckTable"));
const PieCard = lazy(() => import("views/admin/enterprise/components/PieCard"));
const BehaviorChart = lazy(() => import("views/admin/enterprise/components/BehaviorChart"))

const LoadingSpinner = () => (
  <Center h="200px">
    <Spinner size="xl" />
  </Center>
);

export default function Enterprise() {
  const [scoreDistribution, setScoreDistribution] = useState(null);
  const [mostPracticedScenarios, setMostPracticedScenarios] = useState(null);
  const [managerProgress, setManagerProgress] = useState(null);
  const [globalScoreAndScenarios, setGlobalScoreAndScenarios] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [behaviorStatsData, setBehaviorStatsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { getScoreDistribution, getMostPracticedScenarios, getManagerProgress, getGlobalScoreAndScenarios, getEnterpriseKPIs, getBehaviorStats } = useEnterpriseService();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [scoreData, scenariosData, managerData, globalData, kpis, behaviorStats] = await Promise.all([
          getScoreDistribution(),
          getMostPracticedScenarios(),
          getManagerProgress(),
          getGlobalScoreAndScenarios(),
          getEnterpriseKPIs(),
          getBehaviorStats()
        ]);
        setScoreDistribution(scoreData);
        setMostPracticedScenarios(scenariosData);
        setManagerProgress(managerData);
        setGlobalScoreAndScenarios(globalData);
        setKpiData(kpis);
        setBehaviorStatsData(behaviorStats);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);


  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 3, "2xl": 6 }}
        gap='40px'
        mt='10px'
        mb='40px'>
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg='linear-gradient(90deg, #4481EB 0%, #04BEFE 100%)'              
              icon={<Icon w='28px' h='28px' as={MdAddTask} color='white' />}
            />
          }
          name='Managers actifs'
          value={kpiData ? kpiData.activeManagers.toString()+ " / "+ kpiData.totalManagers.toString(): 'N/A'}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg='linear-gradient(90deg, #4481EB 0%, #04BEFE 100%)'
              icon={
                <Icon w='32px' h='32px' as={MdBarChart} color='white' />
              }
            />
          }
          name='Mesure moyenne'
          value={kpiData ? kpiData.globalAverageScore : 'N/A'}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg='linear-gradient(90deg, #4481EB 0%, #04BEFE 100%)'
              icon={
                <Icon w='32px' h='32px' as={MdFileCopy} color='white' />
              }
            />
          }
          name='#Scénarios (Moyenne)'
          value={kpiData ? kpiData.averageScenariosPerManager.toString() : 'N/A'}
        />
      </SimpleGrid>
      <SimpleGrid mt="10px" mb="40px" spacing={{ base: "30px", xl: "30px" }}>
        <Suspense fallback={<LoadingSpinner />}>
          {behaviorStatsData && <BehaviorChart data={behaviorStatsData} />}
        </Suspense>
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap='30px' mb='40px'>
        <Suspense fallback={<LoadingSpinner />}>
          {managerProgress && (
            <ManagersAssiduityTable columnsData={columnsDataAssiduity} tableData={managerProgress}/>
          )}
        </Suspense>
        <Suspense fallback={<LoadingSpinner />}>
          {globalScoreAndScenarios && (
            <TotalSpent 
              monthly={globalScoreAndScenarios.monthly} 
              biWeekly={globalScoreAndScenarios.biWeekly} 
            />
          )}
        </Suspense>
      </SimpleGrid>
      
      <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap='30px' mb='40px'>
        <Suspense fallback={<LoadingSpinner />}>
          {mostPracticedScenarios && (
            <CheckTable columnsData={mostPracticedScenarios.columns} tableData={mostPracticedScenarios.data} />
          )}
        </Suspense>
        <Suspense fallback={<LoadingSpinner />}>
          {scoreDistribution && (
            <PieCard chartData={scoreDistribution} />
          )}
        </Suspense>
      </SimpleGrid>
    </Box>
  );
}